import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthService } from './auth.service';
type QueryParams = Record<string, string | number | boolean>;

/**
 * API service for system-admin app that does NOT require tenant ID.
 * Used for endpoints that manage tenants at a higher level.
 */
@Injectable({
  providedIn: 'root',
})
export class SystemAdminApiService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);

  /**
   * Get authentication headers with Firebase Auth token
   */
  private async getAuthHeaders(): Promise<HttpHeaders> {
    const headers = new HttpHeaders();
    const token = await this.authService.getIdToken();

    if (token) {
      return headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  /**
   * Get App Check token header (placeholder for now)
   */
  private getAppCheckHeaders(headers: HttpHeaders, appCheckToken?: string): HttpHeaders {
    // TODO: Implement App Check token retrieval
    // For now, return headers as-is
    if (appCheckToken) {
      return headers.set('X-App-Check-Token', appCheckToken);
    }
    return headers;
  }

  /**
   * Make a GET request
   * Does NOT add tenantId - system-admin endpoints don't require it
   */
  async get<T>(baseUrl: string, url: string, params?: QueryParams): Promise<Observable<T>> {
    let httpParams = new HttpParams();

    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key].toString());
        }
      });
    }

    const headers = await this.getAuthHeaders();
    const finalHeaders = this.getAppCheckHeaders(headers);

    return this.http.get<T>(`${baseUrl}${url}`, {
      headers: finalHeaders,
      params: httpParams,
    });
  }

  /**
   * Make a POST request
   * Does NOT add tenantId - system-admin endpoints don't require it
   */
  async post<TResponse, TBody extends object>(
    baseUrl: string,
    url: string,
    body: TBody,
  ): Promise<Observable<TResponse>> {
    const headers = await this.getAuthHeaders();
    const finalHeaders = this.getAppCheckHeaders(headers.set('Content-Type', 'application/json'));

    return this.http.post<TResponse>(`${baseUrl}${url}`, body, {
      headers: finalHeaders,
    });
  }

  /**
   * Make a PUT request
   * Does NOT add tenantId - system-admin endpoints don't require it
   */
  async put<TResponse, TBody extends object>(
    baseUrl: string,
    url: string,
    body: TBody,
  ): Promise<Observable<TResponse>> {
    const headers = await this.getAuthHeaders();
    const finalHeaders = this.getAppCheckHeaders(headers.set('Content-Type', 'application/json'));

    return this.http.put<TResponse>(`${baseUrl}${url}`, body, {
      headers: finalHeaders,
    });
  }

  /**
   * Make a DELETE request
   * Does NOT add tenantId - system-admin endpoints don't require it
   */
  async delete<T>(baseUrl: string, url: string): Promise<Observable<T>> {
    const headers = await this.getAuthHeaders();
    const finalHeaders = this.getAppCheckHeaders(headers);

    return this.http.delete<T>(`${baseUrl}${url}`, {
      headers: finalHeaders,
    });
  }
}
